"use client";

import React, { useState, useMemo } from "react";
import dynamic from "next/dynamic";
import { dictionaries } from "@/utils/i18n";
import { HeroSection } from "@/components/Sections/IndustrialLanding/HeroSection";
import { StatsSection } from "@/components/Sections/IndustrialLanding/StatsSection";
import appendImageUrlFromAPI from "@/utils/appendImageUrlFromAPI";
import { parseHtmlTextToJson } from "@/utils/parseHtmlTextToJson";
import {
  parseStructuredListFromHTML,
  stripHTMLKeepBreak,
  stripHTML,
} from "@/utils/stripHTML";
import LazySection from "@/components/LazySection";
import { PROJECT_INFO } from "@/types/projectConsts";

// Dynamically import below-the-fold sections for code splitting
const VideoSection = dynamic(
  () =>
    import("@/components/Sections/IndustrialLanding/VideoSection").then(
      (mod) => mod.VideoSection,
    ),
  { ssr: false },
);
const LocationHighlightSection = dynamic(
  () =>
    import("@/components/Sections/IndustrialLanding/LocationHighlightSection").then(
      (mod) => mod.LocationHighlightSection,
    ),
  { ssr: false },
);
const SubdivisionSection = dynamic(
  () => import("@/components/Sections/IndustrialLanding/SubdivisionSection"),
  { ssr: false },
);
const IndustriesSection = dynamic(
  () =>
    import("@/components/Sections/IndustrialLanding/IndustriesSection").then(
      (mod) => mod.IndustriesSection,
    ),
  { ssr: false },
);
const PdfMultiViewer = dynamic(
  () => import("@/components/Sections/IndustrialLanding/PdfMultiViewer"),
  { ssr: false },
);
const NewsSection = dynamic(
  () =>
    import("@/components/Sections/IndustrialLanding/NewsSection").then(
      (mod) => mod.NewsSection,
    ),
  { ssr: false },
);
const ContactSection = dynamic(
  () => import("@/components/Sections/IndustrialLanding/ContactSection"),
  { ssr: false },
);
const ConsultationFormSection = dynamic(
  () =>
    import("@/components/Sections/IndustrialLanding/ConsultationFormSection").then(
      (mod) => mod.ConsultationFormSection,
    ),
  { ssr: false },
);

interface HomeProps {
  seo: any;
  data: any;
  locale: string;
}

export default function HomeClient({ data, seo, locale }: HomeProps) {
  const dict =
    dictionaries[locale as keyof typeof dictionaries] || dictionaries.vi;
  const landingData =
    dict.industrialLanding || dictionaries.vi.industrialLanding;
  const contactData = dict.contact;

  // Flatten all addon lists to make searching robust across all CMS slots
  const allAddOns = [
    ...(Array.isArray(data?.add_on_data) ? data.add_on_data : []),
  ];

  //#region data map

  // Helper to find add-on by title (ID)
  const getAddOn = (key: string) => {
    return (
      allAddOns.find(
        (item: any) => item?.title?.toLowerCase() === key.toLowerCase(),
      ) || null
    );
  };

  const getAddOn2 = (key: string) => {
    return (
      data?.add_on_data_2?.find(
        (item: any) => item?.title?.toLowerCase() === key.toLowerCase(),
      ) || null
    );
  };
  const getAddOn3 = (key: string) => {
    return (
      data?.add_on_data_3?.find(
        (item: any) => item?.title?.toLowerCase() === key.toLowerCase(),
      ) || null
    );
  };

  // Dynamically configure global PROJECT_INFO properties from project_info JSON API data
  const projectInfo = data?.content;
  if (projectInfo) {
    try {
      const cleanContent = stripHTML(projectInfo).trim();
      const parsed = JSON.parse(cleanContent);
      if (parsed.projectName) PROJECT_INFO.projectName = parsed.projectName;
      if (parsed.address) PROJECT_INFO.address = parsed.address;
      if (parsed.fb) PROJECT_INFO.fb = parsed.fb;
      if (parsed.zalo) PROJECT_INFO.zalo = parsed.zalo;
      if (parsed.youtube) PROJECT_INFO.youtube = parsed.youtube;
      if (parsed.sdt) PROJECT_INFO.sdt = parsed.sdt;
      if (parsed.email) PROJECT_INFO.email = parsed.email;
      if (parsed.website) PROJECT_INFO.website = parsed.website;
    } catch (e) {
      console.error("Failed to parse project_info JSON:", e);
    }
  }

  const getSectionIndex = (key: string, defaultVal: number): number => {
    const addon = getAddOn(key);
    if (!addon) return defaultVal;
    const stt = addon.stt;
    if (stt === undefined || stt === null || stt === "") {
      return defaultVal;
    }
    const val = Number(stt);
    return isNaN(val) ? defaultVal : val;
  };
  // Helper to check if a section is shared (visible)
  const isSectionShared = (key: string, defaultVal = true): boolean => {
    const item = getAddOn(key);
    if (!item) return defaultVal;
    return (
      item.shared !== false &&
      item.shared !== "false" &&
      item.shared !== 0 &&
      item.shared !== "0"
    );
  };

  // Helper to consolidate images from main addon and secondary addons with same title
  const getAddOnImages = (key: string): string[] => {
    const matching = allAddOns.filter(
      (item: any) => item?.title?.toLowerCase() === key.toLowerCase(),
    );
    const images: string[] = [];
    matching.forEach((item: any) => {
      const srcList = [
        item?.picture,
        item?.picture_2,
        item?.picture_3,
        item?.picture_4,
        item?.picture_5,
        item?.picture_6,
      ];
      srcList.forEach((src) => {
        if (src) {
          images.push(appendImageUrlFromAPI({ src, size: "2k" }));
        }
      });
    });
    return images;
  };

  //#endregion

  //#region hero data
  // Extract herosection item from CMS data
  const heroItem =
    allAddOns.find(
      (item: any) =>
        item?.title === "herosection" ||
        item?.title === "tile hero section" ||
        item?.title?.toLowerCase().includes("hero"),
    ) || null;

  // Parse hero title parts
  let headlineScript = "";
  let headlineLine1 = "";
  let headlineLine2 = "";

  if (heroItem?.summary) {
    const parts = heroItem.summary.split("***");
    if (parts.length === 1) {
      headlineScript = parts[0];
      headlineLine1 = "";
      headlineLine2 = "";
    } else if (parts.length === 2) {
      headlineScript = parts[0];
      headlineLine1 = parts[1];
      headlineLine2 = "";
    } else {
      headlineScript = parts[0];
      headlineLine1 = parts[1];
      headlineLine2 = parts[2];
    }
  }

  // Get background image URL
  const backgroundImage = heroItem?.picture
    ? appendImageUrlFromAPI({ src: heroItem.picture, size: "2k" })
    : undefined;

  //#endregion

  //#region stats data
  // Extract StatsSection item from CMS data
  const statsItem =
    allAddOns.find(
      (item: any) =>
        item?.title === "StatsSection" ||
        item?.title?.toLowerCase().includes("stats"),
    ) || null;

  // Parse stats list
  let stats: any[] = [];
  if (statsItem?.summary && statsItem?.content_3) {
    const labels = statsItem.summary.split("***");
    const values = statsItem.content_3.split("***");
    const parsed = labels
      .map((label: string, index: number) => ({
        label: label.trim(),
        value: (values[index] || "").trim(),
      }))
      .filter((item: any) => item.label && item.value);

    if (parsed.length > 0) {
      stats = parsed;
    }
  }

  // Description override
  const statsDescription = stripHTMLKeepBreak(statsItem?.content) || "";
  // Get dynamic image URLs with default fallbacks

  const gateImage = statsItem?.picture_3
    ? appendImageUrlFromAPI({ src: statsItem.picture_3, size: "2k" })
    : undefined;

  //#endregion

  //#region video data
  // Extract video item from CMS data
  const video =
    allAddOns.find(
      (item: any) =>
        item?.title === "video" || item?.title?.toLowerCase().includes("video"),
    ) || null;
  let videoHubText = "";
  let videoPioneerText = "";
  let videoSubText = "";
  const videoDesc = stripHTMLKeepBreak(video?.content) || "";
  if (video?.summary) {
    const parts = video.summary.split("***");
    videoHubText = parts[0]?.trim() || "";
    videoPioneerText = parts[1]?.trim() || "";
    videoSubText = parts[2]?.trim() || "";
  }

  const videoUrl = video?.content_2 || "";

  //#endregion

  //#region   contactform 1

  const contactItem1 =
    allAddOns.find((item: any) => item?.title === "contact-form-1") || null;

  const contactItemData1 = contactItem1
    ? parseHtmlTextToJson<any>(contactItem1.content)[0]
    : null;

  //#endregion

  //#region location data
  // Extract location item from CMS data
  const locationItem =
    allAddOns.find(
      (item: any) =>
        item?.title === "location" ||
        item?.title?.toLowerCase().includes("location"),
    ) || null;

  let parsedLocation = {
    connectTitle1: "",
    connectTitleScript: "",
    connectTitle2: "",
    connectDesc: "",
    timeline: [] as { time: string; unit: string }[],
    labels: {},
  };

  if (locationItem) {
    let connectTitle1 = "";
    let connectTitleScript = "";
    let connectTitle2 = "";
    let connectDesc = "";
    if (locationItem.summary) {
      const parts = locationItem.summary.split("***");
      connectTitle1 = parts[0]?.trim() || "";
      connectTitleScript = parts[1]?.trim() || "";
      connectTitle2 = parts[2]?.trim() || "";
      connectDesc = parts[3]?.trim() || "";
    }

    let timeline: { time: string; unit: string }[] = [];
    if (locationItem.content_3) {
      const matches = locationItem.content_3.matchAll(/(\d+)\s*([^\d]+)/g);
      for (const match of matches) {
        timeline.push({
          time: match[1].trim(),
          unit: match[2].replace(/\*/g, "").trim(),
        });
      }
    }

    let labels = {
      badgeNorthwest: "",
      badgeDucHoaDong: "",
    };

    if (locationItem.content_2) {
      const parts = locationItem.content_2
        .split("***")
        .map((p: string) => p.trim().replace(/^"|"$/g, ""));
      if (parts[0] !== undefined) {
        labels.badgeNorthwest = parts[0];
      }
      if (parts[1] !== undefined) {
        labels.badgeDucHoaDong = parts[1];
      }
    }

    parsedLocation = {
      connectTitle1,
      connectTitleScript,
      connectTitle2,
      connectDesc,
      timeline,
      labels,
    };
  }

  const locationJson = locationItem
    ? parseHtmlTextToJson<any>(locationItem.content)
    : null;
  const listMapPins = locationJson?.[0]?.pin || [];
  const listLabels = locationJson?.[0]?.mapLabel || [];
  const mapImage = locationItem?.picture_2
    ? appendImageUrlFromAPI({ src: locationItem.picture, size: "2k" })
    : undefined;
  //#endregion

  //#region subdivision data
  // Extract subdivision item from CMS data
  const subdivisionItem =
    allAddOns.find(
      (item: any) =>
        item?.title === "subdivision" ||
        item?.title?.toLowerCase().includes("subdivision"),
    ) || null;

  const regions = parseHtmlTextToJson<any>(subdivisionItem?.content) || [];

  const parsedSubdivision = useMemo(() => {
    if (!subdivisionItem) {
      return {
        titleScript: "",
        titleMain: "",
        summary: "",
        table: [] as { label: string; value: string }[],
        bullets: [] as string[],
      };
    }

    const summaryParts = subdivisionItem.summary?.split("***") || [];
    const titleScript = summaryParts[0]?.trim() || "";
    const titleMain = summaryParts[1]?.trim() || "";
    const summary = summaryParts[2]?.trim() || "";

    const table: { label: string; value: string }[] = [];
    if (subdivisionItem.content_3) {
      const parts = subdivisionItem.content_3.split("***");
      for (let i = 0; i < parts.length; i += 2) {
        if (parts[i]) {
          table.push({
            label: parts[i].trim(),
            value: parts[i + 1]?.trim() || "",
          });
        }
      }
    }

    const bullets =
      subdivisionItem.content_2
        ?.split("***")
        .map((b: string) => b.trim())
        .filter(Boolean) || [];

    return {
      titleScript,
      titleMain,
      summary,
      table,
      bullets,
    };
  }, [subdivisionItem]);
  //#endregion

  //#region industries item from CMS data
  const industriesItem =
    allAddOns.find(
      (item: any) =>
        item?.title === "IndustriesSection" ||
        item?.title?.toLowerCase().includes("industries"),
    ) || null;

  let industriesTitleScript = "";
  let industriesTitleNormal = "";
  let industriesSub = "";
  let industriesList: string[] = [];

  if (industriesItem) {
    if (industriesItem.summary) {
      const parts = industriesItem.summary.split("***");
      industriesTitleScript = parts[0]?.trim() || "";
      industriesTitleNormal = parts[1]?.trim() || "";
      industriesSub = parts[2]?.trim() || "";
    }
    if (industriesItem.content_3) {
      industriesList = industriesItem.content_3
        .split("***")
        .map((item: string) => item.trim())
        .filter(Boolean);
    }
  }

  const industriesImages = {
    main: industriesItem?.picture
      ? appendImageUrlFromAPI({ src: industriesItem.picture, size: "xl" })
      : "/images/profession1.png",
    profession2: industriesItem?.picture_2
      ? appendImageUrlFromAPI({ src: industriesItem.picture_2, size: "xl" })
      : "/images/profession2.png",
    profession3: industriesItem?.picture_3
      ? appendImageUrlFromAPI({ src: industriesItem.picture_3, size: "xl" })
      : "/images/profession3.png",
    profession4: industriesItem?.picture_4
      ? appendImageUrlFromAPI({ src: industriesItem.picture_4, size: "xl" })
      : "/images/profession4.png",
    profession5: industriesItem?.picture_5
      ? appendImageUrlFromAPI({ src: industriesItem.picture_5, size: "xl" })
      : "/images/profession5.png",
  };

  //#endregion

  //#region pdf viewer
  const pdfViewer = getAddOn("pdfViewer");
  const pdfAddedData = getAddOn2("pdfViewer");

  const files = useMemo(() => {
    return parseHtmlTextToJson(pdfAddedData?.content) || [];
  }, [pdfViewer]);

  let pdfViewerData = {
    sectionTitleScript: "",
    sectionTitleNormal: "",
    pdfSectionTitleScript: "",
    pdfSectionTitleNormal: "",
    des: "",
    costs: [] as { label: string; value: string; unit: string }[],
  };

  if (pdfViewer?.content)
    pdfViewerData.des = stripHTMLKeepBreak(pdfViewer.content);
  if (pdfViewer?.summary) {
    const parts = pdfViewer.summary.split("***");
    pdfViewerData.sectionTitleScript = parts[0]?.trim() || "";
    pdfViewerData.sectionTitleNormal = parts[1]?.trim() || "";
  }

  if (pdfViewer?.content_2) {
    const parts = pdfViewer.content_2.split("***");
    pdfViewerData.pdfSectionTitleScript = parts[0]?.trim() || "";
    pdfViewerData.pdfSectionTitleNormal = parts[1]?.trim() || "";
  }

  if (pdfViewer?.content_3) {
    const parts = pdfViewer.content_3.split("***").map((x: string) => x.trim());
    for (let i = 0; i < parts.length; i += 3) {
      if (parts[i]) {
        pdfViewerData.costs.push({
          label: parts[i],
          value: parts[i + 1] || "",
          unit: parts[i + 2] || "",
        });
      }
    }
  }
  //#endregion

  //#region news item from CMS data
  const newsItem =
    allAddOns.find(
      (item: any) =>
        item?.title === "news" || item?.title?.toLowerCase().includes("news"),
    ) || null;

  let newsTitleScript = "";
  let newsTitleAmp = "";
  let newsTitleMain = "";

  if (newsItem?.summary) {
    const parts = newsItem.summary.split("***");
    newsTitleScript = parts[0]?.trim() || "";
    newsTitleAmp = parts[1]?.trim() || "";
    newsTitleMain = parts[2]?.trim() || "";
  }

  const newsSectionData = {
    titleScript:
      newsTitleScript || landingData.news?.titleScript || "THÔNG TIN",
    titleAmp: newsTitleAmp || landingData.news?.titleAmp || " & ",
    titleMain: newsTitleMain || landingData.news?.titleMain || "SỰ KIỆN",
  };
  //#endregion

  //#region contact item from CMS data
  const contactItem =
    allAddOns.find(
      (item: any) =>
        item?.title === "contact" ||
        item?.title?.toLowerCase().includes("contact"),
    ) || null;

  let contactTitleScript = "";
  let contactTitleMain = "";
  if (contactItem?.summary) {
    const parts = contactItem.summary.split("***");
    contactTitleScript = parts[0]?.trim() || "";
    contactTitleMain = parts[1]?.trim() || "";
  }

  const contactSubtitle = contactItem?.content_3 || "";

  let contactNamePlaceholder = "";
  let contactPhonePlaceholder = "";
  let contactContentPlaceholder = "";
  if (contactItem?.content_2) {
    const parts = contactItem.content_2.split("***");
    contactNamePlaceholder = parts[0]?.trim() || "";
    contactPhonePlaceholder = parts[1]?.trim() || "";
    contactContentPlaceholder = parts[2]?.trim() || "";
  }

  // Provide local fallbacks for contact section in case API is cached or empty
  const localContactFallbacks: Record<string, any> = {
    vi: {
      titleScript: "Tư Vấn",
      titleMain: "Đăng Ký",
      subtitle:
        "Đăng Ký Ngay Hôm Nay Để Trở Thành Chủ Nhân Tiên Phong Và Nhận Trọn Bộ Thông Tin Chi Tiết Cùng Chính Sách Ưu Đãi Hấp Dẫn",
      namePlaceholder: "Họ Và Tên *",
      phonePlaceholder: "Số Điện Thoại *",
      contentPlaceholder: "Nội Dung Cần Tư Vấn",
    },
    en: {
      titleScript: "Consultation",
      titleMain: "Register",
      subtitle:
        "Register Today To Become A Pioneer Owner And Receive The Full Set Of Detailed Information Along With Attractive Promotion Policies",
      namePlaceholder: "Full Name *",
      phonePlaceholder: "Phone Number *",
      contentPlaceholder: "Consultation Content",
    },
    cn: {
      titleScript: "咨询",
      titleMain: "预约",
      subtitle: "立即预约以成为领先行家，并获取全套项目资料和尊享优惠政策",
      namePlaceholder: "姓名 *",
      phonePlaceholder: "电话号码 *",
      contentPlaceholder: "咨询内容",
    },
  };
  const cFallback = localContactFallbacks[locale] || localContactFallbacks.vi;

  // Extract footer item from CMS data
  const footerItem =
    allAddOns.find(
      (item: any) =>
        item?.title === "footer" ||
        item?.title?.toLowerCase().includes("footer"),
    ) || null;

  let footerAddressLabel = "";
  let footerAddressValue = "";
  if (footerItem?.summary) {
    const parts = footerItem.summary.split("***");
    footerAddressLabel = parts[0]?.trim() || "";
    footerAddressValue = parts[1]?.trim() || "";
  }

  let footerEmailLabel = "";
  let footerEmailValue = "";
  if (footerItem?.content_3) {
    const parts = footerItem.content_3.split("***");
    footerEmailLabel = parts[0]?.trim() || "";
    footerEmailValue = parts[1]?.trim() || "";
  }

  let footerHotlineLabel = "";
  let footerHotlineValue = "";
  if (footerItem?.content_2) {
    const parts = footerItem.content_2.split("***");
    footerHotlineLabel = parts[0]?.trim() || "";
    footerHotlineValue = parts[1]?.trim() || "";
  }
  //#endregion

  //#region  Define all dynamic sections for sorting based on database index
  const sectionsList = [
    {
      id: "herosection",
      index: getSectionIndex("herosection", 0),
      render: () =>
        isSectionShared("herosection") && (
          <HeroSection
            headlineLine1={landingData.headlineLine1}
            headlineLine2={landingData.headlineLine2}
            headlineScript={landingData.headlineScript}
            backgroundImage={backgroundImage}
          />
        ),
    },
    {
      id: "StatsSection",
      index: getSectionIndex("StatsSection", 1),
      render: () =>
        isSectionShared("StatsSection") && (
          <StatsSection
            description={statsDescription}
            stats={stats}
            gateImage={gateImage}
          />
        ),
    },
    {
      id: "video",
      index: getSectionIndex("video", 2),
      render: () =>
        isSectionShared("video") && (
          <LazySection placeholderHeight="650px">
            <VideoSection
              hubText={videoHubText}
              pioneerText={videoPioneerText}
              subText={videoSubText}
              desc={videoDesc}
              videoUrl={videoUrl}
            />
          </LazySection>
        ),
    },
    {
      id: "consultationForm",
      index: getSectionIndex("consultationForm", 3),
      render: () =>
        isSectionShared("consultationForm") && (
          <LazySection placeholderHeight="500px">
            <ConsultationFormSection
              contactItemData1={contactItemData1}
              contact={contactData}
            />
          </LazySection>
        ),
    },
    {
      id: "location",
      index: getSectionIndex("location", 4),
      render: () =>
        isSectionShared("location") && (
          <LazySection placeholderHeight="850px">
            <LocationHighlightSection
              parsedLocation={parsedLocation}
              listMapPins={listMapPins}
              listLabels={listLabels}
              mapImage={mapImage}
            />
          </LazySection>
        ),
    },
    {
      id: "subdivision",
      index: getSectionIndex("subdivision", 5),
      render: () =>
        isSectionShared("subdivision") && (
          <LazySection placeholderHeight="750px">
            <SubdivisionSection
              regions={regions}
              img="/images/subdivision.webp"
              subdivision={parsedSubdivision}
            />
          </LazySection>
        ),
    },
    {
      id: "industries",
      index: getSectionIndex("industries", 6),
      render: () =>
        isSectionShared("industries") && (
          <LazySection placeholderHeight="750px">
            <IndustriesSection
              industriesList={industriesList}
              industriesImages={industriesImages}
              industriesSub={industriesSub}
              industriesTitleNormal={industriesTitleNormal}
              industriesTitleScript={industriesTitleScript}
            />
          </LazySection>
        ),
    },
    {
      id: "pdfViewer",
      index: getSectionIndex("pdfViewer", 7),
      render: () =>
        isSectionShared("pdfViewer") && (
          <LazySection placeholderHeight="800px">
            <PdfMultiViewer files={files} pdfData={pdfViewerData} />
          </LazySection>
        ),
    },
    {
      id: "news",
      index: getSectionIndex("news", 8),
      render: () =>
        isSectionShared("news") && (
          <LazySection placeholderHeight="650px">
            <NewsSection locale={locale} newsData={newsSectionData} />
          </LazySection>
        ),
    },
    {
      id: "contact",
      index: getSectionIndex("contact", 9),
      render: () =>
        isSectionShared("contact") && (
          <LazySection placeholderHeight="550px">
            <ContactSection contact={contactData} />
          </LazySection>
        ),
    },
  ];

  const sortedSections = [...sectionsList].sort((a, b) => a.index - b.index);

  const topSections = sortedSections.filter((sec) => sec.index < 2);
  const bottomSections = sortedSections.filter((sec) => sec.index >= 2);

  //#endregion

  return (
    <div className="min-h-screen overflow-x-hidden selection:bg-[#DBB653] selection:text-[#0d3f4d]">
      <h1 className="sr-only">
        {data?.meta_title ||
          "Cụm công nghiệp Tây Bắc Đức Hòa Đông - Cụm Công Nghiệp Tiên Phong"}
      </h1>

      <main className="relative bg-[#0d3f4d] min-h-screen text-white font-montserrat">
        {/* Render dynamic top sections (Hero and Stats) */}
        {topSections.map((sec) => (
          <React.Fragment key={sec.id}>{sec.render()}</React.Fragment>
        ))}

        {/* ================= PIONEER + LOCATION SHARED BACKGROUND ================= */}
        <div className="relative z-20">
          {/* Smoothly masked background layer */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "url('/images/background.svg') no-repeat center bottom / cover",
              maskImage:
                "linear-gradient(180deg, rgba(0,0,0,0.75) 0%, black 150px)",
              WebkitMaskImage:
                "linear-gradient(180deg, rgba(0,0,0,0.75) 0%, black 150px)",
              zIndex: -1,
            }}
          />

          {/* Linear gradient transition layer at the top of the shared background wrapper */}
          <div
            className="absolute top-0 left-0 right-0 h-32 pointer-events-none z-10"
            style={{
              background:
                "linear-gradient(180deg, #0d3f4d 0%, transparent 100%)",
            }}
          />

          {/* Render sorted dynamic bottom sections (Video, Subdivision, LocationHighlight, etc.) */}
          {bottomSections.map((sec) => (
            <React.Fragment key={sec.id}>{sec.render()}</React.Fragment>
          ))}
        </div>
      </main>
    </div>
  );
}
